/**
 * Expected schema failures from `enhanceAction`.
 *
 * These are returned as data. Throwing a plain Error makes Next.js replace
 * the message in production and makes `onRequestError` report the rejection
 * to Sentry, even though the input was simply invalid.
 */
export const ACTION_INVALID_INPUT = 'invalid_input' as const;

export type ActionSchemaFailure = {
  success: false;
  /**
   * Validation text. Callers that already return `{ success: false, error }`
   * read this field.
   */
  error: string;
  /**
   * Same text as `error`. Create-team and similar callers show `result.message`
   * whenever `result.error` is set, so both fields carry the sentence.
   */
  message: string;
  code: typeof ACTION_INVALID_INPUT;
};

type SchemaParser = {
  safeParseAsync: (value: unknown) => Promise<
    | { success: true; data: unknown }
    | {
        success: false;
        error: { issues?: ReadonlyArray<{ message?: unknown }> };
      }
  >;
};

export function actionSchemaFailure(message: string): ActionSchemaFailure {
  return {
    success: false,
    error: message,
    message,
    code: ACTION_INVALID_INPUT,
  };
}

export function isActionSchemaFailure(
  value: unknown,
): value is ActionSchemaFailure {
  if (!value || typeof value !== 'object') return false;

  const record = value as Record<string, unknown>;

  return (
    record.code === ACTION_INVALID_INPUT &&
    record.success === false &&
    typeof record.error === 'string' &&
    typeof record.message === 'string'
  );
}

/**
 * Turns a schema failure back into a rejection for callers that treat a
 * resolved action as success. The Error is created in the browser, so its
 * message is the validation text rather than Next's production redaction.
 */
export function rejectSchemaFailure<T>(
  value: T,
): Exclude<T, ActionSchemaFailure> {
  if (isActionSchemaFailure(value)) {
    throw new Error(value.error);
  }

  return value as Exclude<T, ActionSchemaFailure>;
}

export async function parseActionSchema<T>(
  schema: SchemaParser,
  value: unknown,
): Promise<
  { ok: true; data: T } | { ok: false; failure: ActionSchemaFailure }
> {
  const parsed = await schema.safeParseAsync(value);

  if (parsed.success) {
    return { ok: true, data: parsed.data as T };
  }

  const firstIssue = parsed.error.issues?.[0];
  const errorText =
    typeof firstIssue?.message === 'string'
      ? firstIssue.message
      : 'Invalid request body';

  return { ok: false, failure: actionSchemaFailure(errorText) };
}
